/**
 * Раздел «Нормоконтроль»: нормативная база — реестр документов (СП, ГОСТ, СНиП, ФЗ,
 * ПП…) и редакции с извлечённым текстом, нарезанным на пункты. Оригинал файла не
 * хранится — база машиночитаемая.
 *
 * Режимы (НК-2a-2, решения Руслана): «Таблица» и «Kanban» — реестр «Нормативов»
 * (вкладки «Что загрузить» нет). У документа — «состояние в базе» base_state
 * (Нужен текст → На разборе → Ждёт подтверждения → В базе → Требует обновления),
 * вычисляемое из версий и юр. статуса; счётчик упоминаний в замечаниях; кнопка
 * «Обновить» автосоздаёт карточки need_text из шифров замечаний. Канбан по base_state,
 * перетаскивание только ready ↔ outdated. «Поиск по пунктам» — третий режим.
 * Данные: /api/norms (backend/app/api/norms.py).
 */
import { api } from '../api.js';
import { canDo } from '../auth.js';
import { createDataTable } from '../components/data-table.js';
import { showNotification } from '../components/notification.js';
import { mountRecordPanel, openRecordPanel, closeRecordPanel } from '../components/record-panel.js';
import { highlightFragment } from '../utils/normcontrol-format.js';
import { createChecksTable, renderMetricsTab, renderRemarksTab } from './normcheck.js';

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const apiErrorText = (e) => e?.detail ?? e?.message ?? 'Ошибка';
const clip = (s) => `<span class="dt-clip" title="${esc(s ?? '')}">${esc(s || '—')}</span>`;

export const DOC_TYPES = ['SP', 'GOST', 'GOST_R', 'SNIP', 'SANPIN', 'FZ', 'PP', 'PRIKAZ', 'RD', 'VSN', 'other'];
export const DOC_TYPE_LABELS = {
  SP: 'СП', GOST: 'ГОСТ', GOST_R: 'ГОСТ Р', SNIP: 'СНиП', SANPIN: 'СанПиН',
  FZ: 'ФЗ', PP: 'ПП', PRIKAZ: 'Приказ', RD: 'РД', VSN: 'ВСН', other: 'Другое',
};
export const STATUSES = ['active', 'replaced', 'cancelled', 'draft', 'unknown'];
export const STATUS_LABELS = {
  active: 'Действует', replaced: 'Заменён', cancelled: 'Отменён', draft: 'Проект', unknown: 'Неизвестно',
};
const PARSE_LABELS = {
  queued: 'В очереди', parsing: 'Разбирается…', done: 'Готово', failed: 'Ошибка', no_text: 'Нет текста',
};
const PARSE_CLASS = {
  queued: 'nc-parse-wait', parsing: 'nc-parse-wait', done: 'nc-parse-ok',
  failed: 'nc-parse-err', no_text: 'nc-parse-warn',
};

const typeLabel = (t) => DOC_TYPE_LABELS[t] ?? t;
const statusLabel = (s) => STATUS_LABELS[s] ?? s;
const fmtDate = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('.') : '');
function fmtDateTime(s) {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s).slice(0, 10);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// Метод проверки актуальности (НК-2b-1)
export const CHECK_METHOD_LABELS = {
  manual: 'ручная', auto_gost: 'ГОСТ (авто)', auto_faufcc: 'ФАУ ФЦС (авто)',
};
const DAY_MS = 86_400_000;
// Цвет колонки «Проверено»: не проверено / старше 30 дней / свежая.
function checkedClass(r) {
  if (!r.status_checked_at) return 'nc-chk-none';
  return (Date.now() - new Date(r.status_checked_at).getTime() > 30 * DAY_MS)
    ? 'nc-chk-stale' : 'nc-chk-fresh';
}
function checkedCell(r) {
  const flag = r.needs_review
    ? ' <span class="nc-chk-review" title="Расхождение с ручной проверкой">⚠</span>' : '';
  const text = r.status_checked_at ? esc(fmtDate(r.status_checked_at)) : 'не проверено';
  return `<span class="nc-chk ${checkedClass(r)}">${text}</span>${flag}`;
}

// Состояние в нашей базе (НК-2a-2 / НОРМ-ЗАГРУЗКА-1). Порядок — колонки канбана.
// «Ждёт подтверждения» (review) убрано: редакция сразу становится текущей (авто).
export const BASE_STATES = ['need_text', 'parsing', 'ready', 'outdated'];
export const BASE_STATE_LABELS = {
  need_text: 'Нужен текст', parsing: 'На разборе',
  ready: 'В базе', outdated: 'Требует обновления',
};
const baseStateLabel = (s) => BASE_STATE_LABELS[s] ?? s;

const MODE_KEY = 'ccpm_norms_mode';
// Канбан-А: доска «Нормативов» убрана — остаются только таблица и поиск по пунктам;
// фильтр по состоянию в базе (base_state) сохраняется в табличном режиме.
const MODES = [
  { key: 'table', label: 'Таблица' },
  { key: 'search', label: 'Поиск по пунктам' },
];
const loadMode = () => { try { return localStorage.getItem(MODE_KEY) || 'table'; } catch { return 'table'; } };
const saveMode = (m) => { try { localStorage.setItem(MODE_KEY, m); } catch { /* не критично */ } };

let _root = null;
let _table = null;
let _mode = 'table';
let _tab = 'checks';     // верхняя вкладка по умолчанию — «Проверки» (НОРМ-ЗАГРУЗКА-1)

// Верхние вкладки раздела «Нормоконтроль». Порядок (НОРМ-ЗАГРУЗКА-1): Проверки ·
// Замечания · Нормативы · Метрики; по умолчанию открывается «Проверки».
// «Метрики» — только для normcheck.decide (НК-3c).
const TABS = [
  { key: 'checks', label: 'Проверки' },
  { key: 'remarks', label: 'Замечания' },
  { key: 'norms', label: 'Нормативы' },
  { key: 'metrics', label: 'Метрики', requires: 'normcheck.decide' },
];
const visibleTabs = () => TABS.filter(t => !t.requires || canDo(t.requires));

export async function initNormControl(container) {
  _table = null;
  _tab = 'checks';
  container.innerHTML = `
    <div class="normcontrol-view tenders-view">
      <header class="tenders-header">
        <h2>Нормоконтроль</h2>
        <div class="nc-tabs" role="tablist">
          ${visibleTabs().map(t => `<button type="button" role="tab" class="tenders-mode" data-nc-tab="${t.key}">${esc(t.label)}</button>`).join('')}
        </div>
      </header>
      <div class="normcontrol-tabpanel"></div>
    </div>`;
  _root = container.querySelector('.normcontrol-view');
  // Открытая правая панель записи (УИ-2b) сжимает тело реестра, а не перекрывает
  // его (переиспользуем --tdi-space из tenders.css, как expertise.js:59).
  _root.addEventListener('ccpm:inspector-resize', (e) => {
    _root.style.setProperty('--tdi-space', `${Math.round(e.detail.width)}px`);
  });
  mountRecordPanel(_root, { key: 'norms', defaultWidth: 560 });
  _root.addEventListener('click', (e) => {
    const t = e.target.closest?.('[data-nc-tab]');
    if (t) { e.preventDefault(); setTab(t.dataset.ncTab); return; }
    const m = e.target.closest?.('[data-nc-mode]');
    if (m) { e.preventDefault(); setMode(m.dataset.ncMode); return; }
    if (e.target.closest?.('[data-nc-new]')) { e.preventDefault(); openNormPanel(null); return; }
    if (e.target.closest?.('[data-nc-sync]')) { e.preventDefault(); runSync(e.target.closest('[data-nc-sync]')); return; }
    if (e.target.closest?.('[data-nc-resplit]')) { e.preventDefault(); runResplit(e.target.closest('[data-nc-resplit]')); return; }
    if (e.target.closest?.('[data-nc-batch]')) { e.preventDefault(); openBatchUpload(); return; }
    const hit = e.target.closest?.('[data-nc-hit]');
    if (hit) { e.preventDefault(); openNormPanel(Number(hit.dataset.ncHit), { focusClause: hit.dataset.ncClauseNo || '', activeTab: 'versions' }); return; }
  });
  setTab('checks', true);
}

/** Переключение верхней вкладки раздела «Нормоконтроль». */
function setTab(tab, force = false) {
  if (!visibleTabs().some(t => t.key === tab)) tab = 'checks';
  const panel = _root.querySelector('.normcontrol-tabpanel');
  if (tab === _tab && !force && panel.children.length) return;
  closeRecordPanel();   // карточку норматива закрываем при смене раздела (УИ-2b)
  _tab = tab;
  _root.querySelectorAll('[data-nc-tab]').forEach((b) => {
    const active = b.dataset.ncTab === tab;
    b.classList.toggle('is-active', active);
    b.setAttribute('aria-selected', String(active));
  });
  _table = null;
  if (tab === 'checks') {
    panel.innerHTML = '<div class="normcheck-panel"></div>';
    createChecksTable(panel.querySelector('.normcheck-panel'), {});
    return;
  }
  if (tab === 'remarks') {
    panel.innerHTML = '<div class="normcheck-panel"></div>';
    renderRemarksTab(panel.querySelector('.normcheck-panel'));
    return;
  }
  if (tab === 'metrics') {
    panel.innerHTML = '<div class="normcheck-panel"></div>';
    renderMetricsTab(panel.querySelector('.normcheck-panel'));
    return;
  }
  // «Нормативы»: панель режимов (Таблица/Поиск) + тело реестра (канбан убран, Канбан-А)
  panel.innerHTML = `
    <div class="nc-norms-bar">
      <div class="tenders-modes" role="tablist">
        ${MODES.map(m => `<button type="button" role="tab" class="tenders-mode" data-nc-mode="${m.key}">${esc(m.label)}</button>`).join('')}
      </div>
      <span class="tenders-hint">Нормативная база для ИИ-нормоконтроля: тексты и пункты документов. Оригиналы файлов не храним.</span>
    </div>
    <div class="normcontrol-body tenders-body"></div>`;
  setMode(loadMode(), true);
}

function setMode(mode, force = false) {
  if (!MODES.some(m => m.key === mode)) mode = 'table';
  if (mode === _mode && !force && _root.querySelector('.normcontrol-body').children.length) return;
  closeRecordPanel();   // карточку норматива закрываем при смене режима (УИ-2b)
  _mode = mode;
  saveMode(mode);
  _root.querySelectorAll('[data-nc-mode]').forEach((b) => {
    const active = b.dataset.ncMode === mode;
    b.classList.toggle('is-active', active);
    b.setAttribute('aria-selected', String(active));
  });
  const body = _root.querySelector('.normcontrol-body');
  body.className = `normcontrol-body tenders-body normcontrol-body--${mode}`;
  _table = null;
  if (mode === 'table') renderRegistry(body);
  else renderSearch(body);
}

/** «Обновить»: пересчитать упоминания и автосоздать карточки need_text из замечаний. */
async function runSync(btn) {
  if (btn) btn.disabled = true;
  try {
    const r = await api.norms.sync();
    const made = r?.created || 0;
    showNotification(made ? `Добавлено карточек «Нужен текст»: ${made}` : 'Нормативы обновлены', 'success');
    if (_mode === 'table') _table?.reload();
  } catch (e) {
    showNotification(apiErrorText(e), 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}

/** «Переразобрать пункты»: пересчитать пункты текущих редакций из сохранённого текста. */
async function runResplit(btn) {
  if (!window.confirm('Пересчитать пункты всех нормативов из сохранённого текста? Файлы перезаливать не нужно.')) return;
  if (btn) btn.disabled = true;
  try {
    const r = await api.norms.resplit();
    const docs = r?.documents || 0;
    const clauses = r?.clauses || 0;
    let msg = `Переразобрано нормативов: ${docs}, пунктов: ${clauses}`;
    const total = r?.findings_total || 0;
    if (total) msg += `; привязок замечаний восстановлено ${r.findings_restored || 0} из ${total}`;
    const orphaned = r?.findings_orphaned || 0;
    if (orphaned) msg += `, осиротело ${orphaned}`;
    showNotification(msg, 'success');
    if (_mode === 'table') _table?.reload();
  } catch (e) {
    showNotification(apiErrorText(e), 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}

// ── Реестр: таблица ───────────────────────────────────────────────────────────

const srcMark = (r) => (r.source === 'auto' ? ' <span class="nc-auto" title="Создан автоматически из замечаний">авто</span>' : '');

const COLUMNS = [
  { key: 'code', label: 'Шифр', width: 200, sortKey: 'code',
    render: r => `<span class="dt-clip" title="${esc(r.code)}">${esc(r.code)}</span>${srcMark(r)}` },
  { key: 'title', label: 'Название', width: 300, render: r => clip(r.title) },
  { key: 'doc_type', label: 'Тип', width: 80, render: r => esc(typeLabel(r.doc_type)) },
  { key: 'base_state', label: 'Состояние в базе', width: 160,
    render: r => `<span class="nc-bs nc-bs-${esc(r.base_state)}">${esc(baseStateLabel(r.base_state))}</span>` },
  { key: 'status', label: 'Юр. статус', width: 120,
    render: r => `<span class="nc-status nc-status-${esc(r.status)}">${esc(statusLabel(r.status))}</span>` },
  { key: 'disciplines', label: 'Разделы', width: 140, render: r => clip((r.disciplines || []).join(', ')) },
  { key: 'mentions_count', label: 'Упоминаний', width: 100, align: 'center',
    render: r => (r.mentions_count ? String(r.mentions_count) : '<span class="nc-muted">—</span>') },
  { key: 'status_checked_at', label: 'Проверено', width: 120, align: 'center',
    render: r => checkedCell(r) },
  { key: 'has_text', label: 'Текст', width: 80, align: 'center',
    render: r => (r.has_text ? `${r.clauses_count} п.` : '<span class="nc-muted">нет</span>') },
];

function registryParams({ q, filters: f }) {
  const params = {};
  if (q) params.q = q;
  if (f.doc_type?.length) params.doc_type = f.doc_type.join(',');
  if (f.status?.length) params.status = f.status.join(',');
  if (f.base_state?.length) params.base_state = f.base_state.join(',');
  if (f.discipline?.length) params.discipline = f.discipline.join(',');
  if (f.no_text?.includes('none')) params.no_text = true;
  if (f.needs_check?.includes('yes')) params.needs_check = true;
  if (f.needs_review?.includes('yes')) params.needs_review = true;
  return params;
}

function renderRegistry(body) {
  const el = document.createElement('div');
  body.innerHTML = '';
  body.appendChild(el);
  _table = createDataTable(el, {
    storageKey: 'ccpm_dt_norms',
    columns: COLUMNS,
    filters: [
      { key: 'base_state', label: 'Состояние', multi: true,
        options: BASE_STATES.map(v => ({ value: v, label: baseStateLabel(v) })) },
      { key: 'doc_type', label: 'Тип', multi: true,
        options: DOC_TYPES.map(v => ({ value: v, label: typeLabel(v) })) },
      { key: 'status', label: 'Юр. статус', multi: true,
        options: STATUSES.map(v => ({ value: v, label: statusLabel(v) })) },
      { key: 'discipline', label: 'Раздел', type: 'list', placeholder: 'Раздел ПД' },
      { key: 'no_text', label: 'Текст', options: [{ value: 'none', label: 'без текста' }] },
      { key: 'needs_check', label: 'Проверка', options: [{ value: 'yes', label: 'нужна проверка' }] },
      { key: 'needs_review', label: 'Расхождения', options: [{ value: 'yes', label: 'есть расхождение' }] },
    ],
    actions: true,
    search: { placeholder: 'Поиск по шифру и названию' },
    pageSizes: [50, 100, 200],
    defaultSort: 'code',
    emptyHint: 'Документов пока нет. Нажмите «Добавить документ» или «Обновить».',
    rowId: r => r.id,
    onRowClick: r => openNormPanel(r.id),
    fetch: async ({ limit, offset, q, filters }) =>
      api.norms.docs({ limit, offset, ...registryParams({ q, filters }) }),
    facets: async ({ q, filters }) => {
      const res = await api.norms.docFacets(registryParams({ q, filters }));
      return { counts: res.counts, summary: res.summary };
    },
  });
  _table.setActions(canDo('norm.write') ? regActions() : '');
}

/** Кнопки над реестром: «Обновить», «Пакетная загрузка текстов» + «Добавить документ». */
function regActions() {
  return '<button type="button" class="dt-btn" data-nc-sync>Обновить</button>'
    + '<button type="button" class="dt-btn" data-nc-resplit>Переразобрать пункты</button>'
    + '<button type="button" class="dt-btn" data-nc-batch>Пакетная загрузка текстов</button>'
    + '<button type="button" class="dt-btn cp-primary" data-nc-new>Добавить документ</button>';
}

// ── Вкладка «Поиск по пунктам» (межкументный FTS по текущим редакциям) ────────

let _searchSeq = 0;

function renderSearch(body) {
  _table = null;
  body.innerHTML = `
    <div class="nc-search">
      <form class="nc-search-form" data-nc-search-form>
        <input type="search" name="q" class="nc-search-q" placeholder="Слова, номер пункта или шифр (напр. «расстояние», «7.2», «СП 42.13330»)" autocomplete="off" />
        <select name="doc_type" class="modal-select nc-search-filter">
          <option value="">Все типы</option>
          ${DOC_TYPES.map((t) => `<option value="${t}">${esc(typeLabel(t))}</option>`).join('')}
        </select>
        <select name="status" class="modal-select nc-search-filter">
          <option value="">Любой статус</option>
          ${STATUSES.map((s) => `<option value="${s}">${esc(statusLabel(s))}</option>`).join('')}
        </select>
        <input name="discipline" class="nc-search-filter" placeholder="Раздел ПД (напр. КР)" />
        <button type="submit" class="dt-btn cp-primary">Найти</button>
      </form>
      <p class="nc-muted nc-search-note">Ищем по пунктам подтверждённых (текущих) редакций. Загрузите и подтвердите редакцию, чтобы документ попал в поиск.</p>
      <div class="nc-search-results" data-nc-search-results></div>
    </div>`;
  const form = body.querySelector('[data-nc-search-form]');
  form.addEventListener('submit', (e) => { e.preventDefault(); runSearch(form); });
  form.querySelector('[name="q"]').focus();
}

async function runSearch(form) {
  const host = form.closest('.nc-search').querySelector('[data-nc-search-results]');
  const params = {
    q: form.querySelector('[name="q"]').value.trim(),
    doc_type: form.querySelector('[name="doc_type"]').value,
    status: form.querySelector('[name="status"]').value,
    discipline: form.querySelector('[name="discipline"]').value.trim(),
    limit: 100,
  };
  if (params.q.length < 2) {
    host.innerHTML = '<p class="nc-muted">Введите не меньше двух символов.</p>';
    return;
  }
  const seq = ++_searchSeq;
  host.innerHTML = '<div class="loading-spinner">Поиск…</div>';
  let res;
  try { res = await api.norms.search(params); }
  catch (e) { if (seq === _searchSeq) host.innerHTML = `<p class="cp-error">Ошибка: ${esc(apiErrorText(e))}</p>`; return; }
  if (seq !== _searchSeq) return;  // пришёл ответ на устаревший запрос
  if (!res.items.length) {
    host.innerHTML = '<p class="nc-muted">Ничего не найдено.</p>';
    return;
  }
  host.innerHTML = `<p class="nc-muted">Найдено пунктов: ${res.total}${res.total > res.items.length ? ` (показаны первые ${res.items.length})` : ''}</p>
    <ul class="nc-hits">${res.items.map(hitItem).join('')}</ul>`;
}

function hitItem(h) {
  const no = h.clause_no ? `п. ${esc(h.clause_no)}` : '(без номера)';
  const head = h.heading ? ` — ${esc(h.heading)}` : '';
  return `<li class="nc-hit">
    <button type="button" class="nc-hit-head" data-nc-hit="${h.doc_id}" data-nc-clause-no="${esc(h.clause_no || '')}">
      <span class="nc-hit-code">${esc(h.code)}</span>
      <span class="nc-hit-no">${no}</span>${head}
    </button>
    <div class="nc-hit-frag">${highlightFragment(h.fragment)}</div>
  </li>`;
}

// ── Общий диалог (как в counterparties.js) ──────────────────────────────────

function openDialog({ title, bodyHtml, confirmLabel = 'Сохранить', wide = false, onMount, onConfirm }) {
  return new Promise((resolve) => {
    const dlg = document.createElement('dialog');
    dlg.className = 'app-modal cp-modal' + (wide ? ' cp-modal-wide' : '');
    dlg.innerHTML = `
      <form method="dialog" class="modal-form">
        <h3 class="modal-title">${esc(title)}</h3>
        <div class="cp-dialog-body nc-dialog-body">${bodyHtml}</div>
        <div class="modal-actions">
          <button type="button" class="modal-cancel">Закрыть</button>
          ${onConfirm ? `<button type="submit" class="modal-confirm">${esc(confirmLabel)}</button>` : ''}
        </div>
      </form>`;
    document.body.appendChild(dlg);
    const close = (v) => { try { dlg.close(); } catch { /* уже закрыт */ } dlg.remove(); resolve(v); };
    dlg.querySelector('.modal-cancel').addEventListener('click', () => close(null));
    dlg.addEventListener('cancel', (e) => { e.preventDefault(); close(null); });
    dlg.querySelector('form').addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!onConfirm) { close(null); return; }
      const btn = dlg.querySelector('.modal-confirm');
      btn.disabled = true;
      const res = await onConfirm(dlg);
      btn.disabled = false;
      if (res !== false) close(res);
    });
    onMount?.(dlg, close);
    dlg.showModal();
  });
}

const field = (name, label, value, attrs = '') =>
  `<label class="modal-field"><span class="modal-field-label">${esc(label)}</span>
     <input name="${name}" value="${esc(value ?? '')}" ${attrs} /></label>`;

// ── Карточка документа — правая панель (УИ-2b) ────────────────────────────────
// Было: openCard/openDocForm открывали центральные <dialog>. Теперь карточка и
// форма документа живут в правой панели (components/record-panel.js) с тремя
// вкладками: «Данные» (форма, createDoc/patchDoc), «Актуальность» (НК-2b-1) и
// «Редакции» (загрузка, опрос parse_status, подтверждение, пункты/поиск).
// Вторичные окна (загрузка файла, отметка проверки, текст редакции) по-прежнему
// открываются через openDialog поверх панели.

/** Открыть карточку норматива в правой панели. id=null — создание. */
async function openNormPanel(id, opts = {}) {
  if (id == null) { showNormPanel(null, { activeTab: 'data' }); return; }
  let doc;
  try { doc = await api.norms.doc(id); }
  catch (e) { showNotification(apiErrorText(e), 'error'); return; }
  showNormPanel(doc, opts);
}

function showNormPanel(doc, { activeTab = 'data', focusClause = '' } = {}) {
  let pendingFocus = focusClause;   // пункт из поиска — фокусируем один раз
  openRecordPanel(doc, {
    title: (rec) => (rec?.code ? rec.code : 'Новый документ в реестр'),
    activeTab,
    onClose: () => _table?.setSelected?.(null),
    tabs: [
      {
        key: 'data',
        label: 'Данные',
        readonly: !canDo('norm.write'),
        saveLabel: doc ? 'Сохранить' : 'Добавить',
        fields: (rec) => dataFields(rec),
        readLines: (rec) => docReadLines(rec),
        onSave: (v, { isCreate, record }) => {
          const body = readDataFields(v);
          if (!body.code) throw new Error('Укажите шифр');
          return isCreate ? api.norms.createDoc(body) : api.norms.patchDoc(record.id, body);
        },
        reload: (saved) => api.norms.doc(saved.id),
        onSaved: (_fresh, { isCreate, setTab }) => {
          showNotification(isCreate ? 'Документ добавлен' : 'Документ сохранён', 'success');
          _table?.reload();
          if (isCreate) setTab('versions');   // сразу загрузить редакцию (прежний смысл openCard)
        },
      },
      { key: 'actuality', label: 'Актуальность', render: (rec, ctx) => renderActualityTab(rec, ctx) },
      {
        key: 'versions', label: 'Редакции',
        render: (rec, ctx) => { ctx.focusClause = pendingFocus; pendingFocus = ''; return renderVersionsTab(rec, ctx); },
      },
    ],
  });
  _table?.setSelected?.(doc?.id ?? null);
}

// ── Вкладка «Данные» (форма документа) ─────────────────────────────────────────

function dataFields(doc) {
  return [
    { name: 'code', label: 'Шифр (как в документе)', required: true, placeholder: 'СП 42.13330.2016', value: doc?.code ?? '' },
    { name: 'title', label: 'Название', value: doc?.title ?? '' },
    { name: 'doc_type', label: 'Тип', type: 'select', value: doc?.doc_type ?? 'other',
      options: DOC_TYPES.map(t => ({ value: t, label: typeLabel(t) })) },
    { name: 'status', label: 'Юр. статус', type: 'select', value: doc?.status ?? 'unknown',
      options: STATUSES.map(s => ({ value: s, label: statusLabel(s) })) },
    { name: 'effective_from', label: 'Дата введения', type: 'date',
      value: doc?.effective_from ? String(doc.effective_from).slice(0, 10) : '' },
    { name: 'disciplines', label: 'Разделы ПД (через запятую)', placeholder: 'АР, КР, ИОС4',
      value: (doc?.disciplines || []).join(', ') },
    { name: 'mandatory', label: 'Из перечня обязательных', type: 'select', value: doc?.mandatory ? '1' : '0',
      options: [{ value: '0', label: 'Нет' }, { value: '1', label: 'Да' }] },
    { name: 'note', label: 'Примечание', type: 'textarea', rows: 2, wide: true, value: doc?.note ?? '' },
  ];
}

function readDataFields(v) {
  return {
    code: (v.code ?? '').trim(),
    title: v.title ? String(v.title).trim() : null,
    doc_type: v.doc_type,
    status: v.status,
    effective_from: v.effective_from || null,
    disciplines: (v.disciplines ?? '').split(',').map(s => s.trim()).filter(Boolean),
    mandatory: v.mandatory === '1',
    note: v.note ? String(v.note).trim() : null,
  };
}

function docReadLines(doc) {
  if (!doc) return [];
  return [
    { label: 'Нормализованный шифр', value: doc.code_norm },
    doc.replaces_text ? { label: 'Взамен', value: doc.replaces_text } : null,
    { label: 'Упоминаний в замечаниях', value: doc.mentions_count || '—' },
  ].filter(Boolean);
}

// ── Вкладка «Актуальность» (НК-2b-1): статус, история, ручная отметка, авто-очередь ──

function renderActualityTab(doc, ctx) {
  const host = ctx.host;
  const canCheck = canDo('norm.check');
  host.innerHTML = `
    <div class="rp-section-head">
      <h4 class="rp-h">Актуальность</h4>
      <div class="nc-act-actions">
        ${canCheck ? '<button type="button" class="dt-btn" data-nc-check-request>Проверить автоматически</button>' : ''}
        ${canCheck ? '<button type="button" class="dt-btn cp-primary" data-nc-check-mark>Отметить проверку</button>' : ''}
      </div>
    </div>
    <div class="nc-actuality"><div class="nc-act-body" data-nc-actuality><div class="loading-spinner">Загрузка…</div></div></div>`;
  let alive = true;
  ctx.onTeardown(() => { alive = false; });

  async function loadActuality() {
    const h = host.querySelector('[data-nc-actuality]');
    if (!h) return;
    let checks = [];
    try { checks = await api.norms.checks(ctx.record.id); }
    catch (e) { if (alive) h.innerHTML = `<p class="cp-error">Ошибка: ${esc(apiErrorText(e))}</p>`; return; }
    if (!alive) return;
    renderActuality(h, ctx.record, checks);
  }

  host.addEventListener('click', async (e) => {
    const req = e.target.closest?.('[data-nc-check-request]');
    if (req) {
      e.preventDefault();
      req.disabled = true;
      try {
        ctx.setRecord(await api.norms.checkRequest(ctx.record.id));
        showNotification('Документ поставлен в очередь до ночного прогона', 'success');
        _table?.reload();
        await loadActuality();
      } catch (err) { showNotification(apiErrorText(err), 'error'); }
      finally { if (alive) req.disabled = false; }
      return;
    }
    const mark = e.target.closest?.('[data-nc-check-mark]');
    if (mark) {
      e.preventDefault();
      const saved = await openCheckForm(ctx.record);
      if (!saved || !alive) return;
      ctx.setRecord(saved);
      showNotification('Проверка отмечена', 'success');
      _table?.reload();   // статус мог сделать документ «Требует обновления»
      await loadActuality();
    }
  });

  return loadActuality();
}

function renderActuality(host, d, checks) {
  const checked = d.status_checked_at
    ? `проверено ${esc(fmtDate(d.status_checked_at))}${d.status_source ? ` (${esc(d.status_source)})` : ''}`
    : 'ещё не проверялось';
  const queued = d.check_requested
    ? '<div class="nc-act-queued">в очереди на авто-проверку (до ночного прогона)</div>' : '';
  const review = d.needs_review
    ? '<div class="nc-act-review">⚠ расхождение с ручной проверкой — требует внимания</div>' : '';
  const hist = checks.length
    ? `<ol class="nc-check-hist">${checks.map(checkRow).join('')}</ol>`
    : '<p class="nc-muted">Проверок пока не было.</p>';
  host.innerHTML = `
    <div class="nc-act-status">
      <span class="nc-status nc-status-${esc(d.status)}">${esc(statusLabel(d.status))}</span>
      <span class="nc-muted">${checked}</span>
    </div>
    ${queued}${review}
    <h5 class="nc-check-hist-title">История проверок</h5>${hist}`;
}

// ── Вкладка «Редакции»: редакции, загрузка, опрос parse_status, подтверждение, пункты ──

function renderVersionsTab(doc, ctx) {
  const host = ctx.host;
  host.innerHTML = `
    <div class="rp-section-head">
      <h4 class="rp-h">Редакции</h4>
      ${canDo('norm.write') ? '<button type="button" class="dt-btn cp-primary" data-nc-upload>Загрузить редакцию</button>' : ''}
    </div>
    <div class="nc-versions" data-nc-versions><div class="loading-spinner">Загрузка...</div></div>
    <div class="rp-section-head">
      <h4 class="rp-h">Пункты</h4>
      <input type="search" class="nc-clause-q" data-nc-clause-q placeholder="Поиск по пункту и тексту" disabled>
    </div>
    <div class="nc-clauses" data-nc-clauses><p class="nc-muted">Подтвердите редакцию, чтобы увидеть пункты.</p></div>`;

  const state = { versions: [], clauseVersionId: null, poll: null, ct: null, alive: true, focus: ctx.focusClause || '' };
  ctx.onTeardown(() => { state.alive = false; clearTimeout(state.poll); clearTimeout(state.ct); });

  const qEl = host.querySelector('[data-nc-clause-q]');
  qEl.addEventListener('input', (e) => {
    clearTimeout(state.ct);
    const q = e.target.value;
    state.ct = setTimeout(() => loadClauses(state.clauseVersionId, q), 300);
  });
  host.addEventListener('click', onVersionsClick);

  async function loadVersions() {
    let rows;
    try { rows = await api.norms.versions(ctx.record.id); }
    catch (e) { showNotification(apiErrorText(e), 'error'); return; }
    if (!state.alive) return;
    state.versions = rows;
    renderVersions();
    // пункты показываем по текущей редакции, иначе по последней разобранной
    const cur = rows.find(v => v.is_current) || rows.find(v => v.parse_status === 'done');
    if (cur && cur.id !== state.clauseVersionId) {
      state.clauseVersionId = cur.id;
      qEl.disabled = cur.parse_status !== 'done';
      const focus = state.focus;   // при открытии из поиска — сразу нужный пункт
      state.focus = '';
      if (focus) qEl.value = focus;
      loadClauses(cur.id, focus);
    }
    // пока что-то в очереди/разборе — опрашиваем статус
    const pending = rows.some(v => v.parse_status === 'queued' || v.parse_status === 'parsing');
    if (pending && state.alive) state.poll = setTimeout(loadVersions, 2000);
  }

  function renderVersions() {
    const h = host.querySelector('[data-nc-versions]');
    if (!h) return;
    if (!state.versions.length) {
      h.innerHTML = '<p class="nc-muted">Редакций пока нет. Загрузите PDF, DOCX, TXT или MD.</p>';
      return;
    }
    h.innerHTML = `<table class="nc-ver-table"><thead><tr>
        <th>Редакция</th><th>Файл</th><th>Разбор</th><th>Пунктов</th><th>Текущая</th><th></th>
      </tr></thead><tbody>${state.versions.map(versionRow).join('')}</tbody></table>`;
  }

  async function loadClauses(versionId, q) {
    const h = host.querySelector('[data-nc-clauses]');
    if (!h || !versionId) return;
    let res;
    try { res = await api.norms.clauses(versionId, { q, limit: 500 }); }
    catch (e) { h.innerHTML = `<p class="cp-error">Ошибка: ${esc(apiErrorText(e))}</p>`; return; }
    if (!state.alive) return;
    if (!res.items.length) {
      h.innerHTML = `<p class="nc-muted">${q ? 'Ничего не найдено.' : 'В этой редакции нет пунктов.'}</p>`;
      return;
    }
    h.innerHTML = `<p class="nc-muted">Найдено пунктов: ${res.total}${res.total > res.items.length ? ` (показаны первые ${res.items.length})` : ''}</p>
      <ul class="nc-clauses-list">${res.items.map(clauseItem).join('')}</ul>`;
  }

  async function onVersionsClick(e) {
    const upload = e.target.closest?.('[data-nc-upload]');
    if (upload) {
      e.preventDefault();
      const saved = await openUploadForm(ctx.record);
      if (saved && state.alive) { state.clauseVersionId = null; await loadVersions(); _table?.reload(); }
      return;
    }
    const clause = e.target.closest?.('[data-nc-clause]');
    if (clause) {
      e.preventDefault();
      const t = host.querySelector(`[data-nc-clause-text="${clause.dataset.ncClause}"]`);
      if (t) t.hidden = !t.hidden;
      return;
    }
    const textBtn = e.target.closest?.('[data-nc-text]');
    if (textBtn) { e.preventDefault(); showText(Number(textBtn.dataset.ncText)); }
  }

  async function showText(versionId) {
    let res;
    try { res = await api.norms.versionText(versionId); }
    catch (e) { showNotification(apiErrorText(e), 'error'); return; }
    await openDialog({
      title: 'Текст редакции',
      wide: true,
      bodyHtml: `<pre class="nc-fulltext">${esc(res.text || '(пусто)')}</pre>`,
    });
  }

  return loadVersions();
}

function versionRow(v) {
  const label = v.label || (v.effective_from ? `ред. ${fmtDate(v.effective_from)}` : `#${v.id}`);
  const status = `<span class="nc-parse ${PARSE_CLASS[v.parse_status] || ''}">${esc(PARSE_LABELS[v.parse_status] || v.parse_status)}</span>`
    + (v.parse_status === 'failed' && v.parse_error ? `<span class="nc-parse-detail" title="${esc(v.parse_error)}"> ⓘ</span>` : '');
  // Подтверждения редакции больше нет (НОРМ-ЗАГРУЗКА-1): после разбора редакция сразу
  // текущая (✓). Столбец действий — только «Текст».
  const showBtn = v.has_text ? `<button type="button" class="dt-btn" data-nc-text="${v.id}">Текст</button>` : '';
  return `<tr data-ver="${v.id}">
    <td>${esc(label)}</td>
    <td>${clip(v.filename)}${v.pages ? ` <span class="nc-muted">(${v.pages} с.)</span>` : ''}</td>
    <td>${status}</td>
    <td style="text-align:center">${v.clauses_count || 0}</td>
    <td style="text-align:center">${v.is_current ? '✓' : ''}</td>
    <td class="nc-ver-actions">${showBtn}</td>
  </tr>`;
}

function clauseItem(c) {
  const head = [c.clause_no, c.heading].filter(Boolean).join(' — ') || '(без номера)';
  return `<li class="nc-clause">
    <button type="button" class="nc-clause-head" data-nc-clause="${c.id}">${esc(head)}</button>
    <div class="nc-clause-text" data-nc-clause-text="${c.id}" hidden>${esc(c.text)}</div>
  </li>`;
}

// ── Загрузка редакции ──────────────────────────────────────────────────────────

async function openUploadForm(doc) {
  return openDialog({
    title: `Загрузить редакцию: ${doc.code}`,
    confirmLabel: 'Загрузить',
    bodyHtml: `
      <label class="modal-field"><span class="modal-field-label">Файл (PDF, DOCX, TXT, MD; до 50 МБ)</span>
        <input type="file" name="file" accept=".pdf,.docx,.txt,.md,.markdown" required /></label>
      ${field('label', 'Метка редакции', '', 'placeholder="ред. 2016 / Изм. №1"')}
      ${field('effective_from', 'Дата введения редакции', '', 'type="date"')}
      <p class="nc-muted">После загрузки текст извлекается в фоне и режется на пункты. Оригинал файла не сохраняется.</p>`,
    onConfirm: async (dlg) => {
      const fileInput = dlg.querySelector('[name="file"]');
      const file = fileInput.files?.[0];
      if (!file) { showNotification('Выберите файл', 'error'); return false; }
      const meta = {
        label: dlg.querySelector('[name="label"]').value.trim(),
        effective_from: dlg.querySelector('[name="effective_from"]').value,
      };
      try {
        const res = await api.norms.uploadVersion(doc.id, file, meta);
        showNotification('Файл загружен, идёт разбор', 'success');
        return res;
      } catch (e) { showNotification(apiErrorText(e), 'error'); return false; }
    },
  });
}

// ── Пакетная загрузка текстов (НОРМ-ТЕКСТ-1) ──────────────────────────────────
// Человек выбирает несколько публичных файлов; система сопоставляет каждый с
// реестром по шифру (имя файла / первые строки текста) и грузит редакцию. Файлы
// шлём ПО ОДНОМУ (nginx 50m/30s), с прогрессом N/total. Отчёт: сопоставлено и
// загружено, пропущено (уже есть текст), не сопоставлено (ручной выбор документа
// из реестра + дозагрузка). После разбора — подтверждение по одному и «Подтвердить
// все загруженные»; нарезка бывает неидеальной, поэтому молча текущими не делаем.

const OUTCOME_TITLES = {
  matched: 'Сопоставлено и загружено',
  skipped: 'Пропущено (текст уже есть)',
  unmatched: 'Не сопоставлено',
};

function openBatchUpload() {
  const p = openDialog({
    title: 'Пакетная загрузка текстов нормативов',
    wide: true,
    bodyHtml: `
      <div class="nc-batch">
        <div class="nc-batch-phase" data-nc-phase="select">
          <label class="modal-field"><span class="modal-field-label">Файлы нормативов (PDF, DOCX, TXT, MD; до 50 МБ каждый)</span>
            <input type="file" name="files" accept=".pdf,.docx,.txt,.md,.markdown" multiple /></label>
          <p class="nc-muted">Назовите файлы по шифру (напр. «СП 42.13330.2016.pdf») — так они сопоставятся с реестром сами. Файлы грузятся по одному. Оригиналы не храним, только текст и пункты.</p>
          <div class="nc-batch-foot"><button type="button" class="dt-btn cp-primary" data-nc-batch-start disabled>Загрузить</button></div>
        </div>
        <div class="nc-batch-phase" data-nc-phase="progress" hidden>
          <p class="nc-batch-progress">Загрузка: <b data-nc-done>0</b> / <span data-nc-total>0</span></p>
          <div class="nc-batch-track"><div class="nc-batch-fill" data-nc-bar style="width:0"></div></div>
          <p class="nc-muted nc-batch-cur" data-nc-cur></p>
        </div>
        <div class="nc-batch-phase" data-nc-phase="report" hidden>
          <div class="nc-batch-summary" data-nc-summary></div>
          <div class="nc-batch-report" data-nc-report></div>
          <div class="nc-batch-foot" data-nc-report-foot></div>
        </div>
      </div>`,
    onMount: (dlg) => runBatchUpload(dlg),
  });
  // Реестр мог измениться (новые редакции/подтверждения) — обновим после закрытия.
  p.then(() => { if (_mode === 'table') _table?.reload(); });
  return p;
}

/** Контроллер диалога пакетной загрузки (замыкание на DOM диалога). */
function runBatchUpload(dlg) {
  const root = dlg.querySelector('.nc-batch');
  const state = { files: [], results: [], done: 0, alive: true, poll: null };
  const $ = (sel) => root.querySelector(sel);
  const phase = (name) => root.querySelectorAll('[data-nc-phase]').forEach((el) => {
    el.hidden = el.dataset.ncPhase !== name;
  });
  dlg.addEventListener('close', () => { state.alive = false; clearTimeout(state.poll); });

  const fileInput = $('[name="files"]');
  const startBtn = $('[data-nc-batch-start]');
  fileInput.addEventListener('change', () => { startBtn.disabled = !fileInput.files?.length; });
  startBtn.addEventListener('click', () => {
    state.files = Array.from(fileInput.files || []);
    if (!state.files.length) { showNotification('Выберите файлы', 'error'); return; }
    upload();
  });
  // Делегирование действий на отчёте (подтвердить, подобрать документ, дозагрузить).
  $('[data-nc-report]').addEventListener('click', onReportClick);
  // Enter в поле подбора ищет документы, не отправляя форму диалога (иначе закроется).
  $('[data-nc-report]').addEventListener('keydown', (e) => {
    const q = e.target.closest?.('[data-nc-pick-q]');
    if (q && e.key === 'Enter') { e.preventDefault(); pickDocs(Number(q.closest('[data-nc-pick-idx]').dataset.ncPickIdx)); }
  });

  async function upload() {
    phase('progress');
    const total = state.files.length;
    $('[data-nc-total]').textContent = String(total);
    for (let i = 0; i < state.files.length; i++) {
      if (!state.alive) return;
      const file = state.files[i];
      $('[data-nc-cur]').textContent = `Загружается: ${file.name}`;
      let res;
      try {
        res = await api.norms.uploadMatch(file);
      } catch (e) {
        res = { filename: file.name, outcome: 'unmatched', candidate_code: null, error: apiErrorText(e) };
      }
      res.fileIndex = i;
      if (res.version_id) res.parse = { parse_status: res.duplicate ? 'parsing' : 'queued' };
      state.results.push(res);
      state.done = i + 1;
      if (!state.alive) return;
      $('[data-nc-done]').textContent = String(state.done);
      $('[data-nc-bar]').style.width = `${Math.round((state.done / total) * 100)}%`;
    }
    if (!state.alive) return;
    phase('report');
    renderAll();
    pollMatched();
  }

  function byOutcome(outcome) { return state.results.filter((r) => r.outcome === outcome); }

  function renderAll() { renderSummary(); renderReport(); renderFoot(); }

  function renderSummary() {
    const counts = ['matched', 'skipped', 'unmatched'].map((o) => {
      const n = byOutcome(o).length;
      return n ? `<span class="nc-batch-chip nc-batch-chip--${o}">${esc(OUTCOME_TITLES[o])}: ${n}</span>` : '';
    }).filter(Boolean).join('');
    $('[data-nc-summary]').innerHTML = counts || '<span class="nc-muted">Нет файлов.</span>';
  }

  function renderReport() {
    const host = $('[data-nc-report]');
    const groups = ['matched', 'skipped', 'unmatched'].map((o) => {
      const rows = byOutcome(o);
      if (!rows.length) return '';
      const body = rows.map((r) => batchRow(r, o)).join('');
      return `<section class="nc-batch-group"><h4 class="nc-batch-group-h">${esc(OUTCOME_TITLES[o])}</h4>
        <ul class="nc-batch-list">${body}</ul></section>`;
    }).join('');
    host.innerHTML = groups;
  }

  function batchRow(r, outcome) {
    const idx = state.results.indexOf(r);
    const name = `<span class="nc-batch-fname" title="${esc(r.filename)}">${esc(r.filename)}</span>`;
    if (outcome === 'matched') {
      const ps = r.parse?.parse_status || 'queued';
      const parse = `<span class="nc-parse ${PARSE_CLASS[ps] || ''}">${esc(PARSE_LABELS[ps] || ps)}</span>`;
      const cnt = r.parse?.clauses_count != null && ps === 'done' ? ` <span class="nc-muted">(${r.parse.clauses_count} п.)</span>` : '';
      const code = r.doc_code ? `<span class="nc-batch-code">${esc(r.doc_code)}</span>` : '';
      const dup = r.duplicate ? ' <span class="nc-muted">(уже был)</span>' : '';
      // Подтверждения нет (НОРМ-ЗАГРУЗКА-1): после разбора редакция сразу текущая («в базе»).
      const act = r.parse?.is_current ? '<span class="nc-batch-ok">✓ в базе</span>' : '';
      return `<li class="nc-batch-item">${code} ${name}${dup} — ${parse}${cnt} <span class="nc-batch-act">${act}</span></li>`;
    }
    if (outcome === 'skipped') {
      const code = r.doc_code ? `<span class="nc-batch-code">${esc(r.doc_code)}</span>` : '';
      return `<li class="nc-batch-item">${code} ${name} <span class="nc-muted">— ${esc(r.detail || 'уже есть текст')}</span></li>`;
    }
    // unmatched: ручной выбор документа из реестра + дозагрузка
    const suggest = r.candidate_code || '';
    const reason = r.error ? `ошибка: ${esc(r.error)}` : esc(r.detail || 'шифр не распознан');
    return `<li class="nc-batch-item nc-batch-item--unmatched">
      <div class="nc-batch-urow">${name} <span class="nc-muted">— ${reason}</span></div>
      <div class="nc-batch-pick" data-nc-pick-idx="${idx}">
        <input type="search" class="nc-batch-pick-q" data-nc-pick-q value="${esc(suggest)}" placeholder="Шифр или название документа реестра" />
        <button type="button" class="dt-btn" data-nc-pick-go="${idx}">Подобрать</button>
      </div>
      <div class="nc-batch-pick-res" data-nc-pick-res="${idx}"></div></li>`;
  }

  function renderFoot() {
    // Кнопки «Подтвердить все загруженные» больше нет (НОРМ-ЗАГРУЗКА-1): разобранная
    // редакция сразу становится текущей, подтверждать нечего.
    $('[data-nc-report-foot]').innerHTML = '';
  }

  async function pollMatched() {
    clearTimeout(state.poll);
    const pending = byOutcome('matched').filter(
      (r) => r.version_id && ['queued', 'parsing'].includes(r.parse?.parse_status));
    if (!pending.length) return;
    await Promise.all(pending.map(async (r) => {
      try { r.parse = await api.norms.version(r.version_id); } catch { /* повторим на следующем цикле */ }
    }));
    if (!state.alive) return;
    renderSummary(); renderReport(); renderFoot();
    const still = byOutcome('matched').some((r) => ['queued', 'parsing'].includes(r.parse?.parse_status));
    if (still && state.alive) state.poll = setTimeout(pollMatched, 2000);
  }

  async function onReportClick(e) {
    const go = e.target.closest?.('[data-nc-pick-go]');
    if (go) { await pickDocs(Number(go.dataset.ncPickGo)); return; }
    const choose = e.target.closest?.('[data-nc-pick-doc]');
    if (choose) { await manualUpload(Number(choose.dataset.ncPickDoc), Number(choose.dataset.docId), choose.dataset.docCode); return; }
  }

  async function pickDocs(idx) {
    const host = $(`[data-nc-pick-res="${idx}"]`);
    const q = root.querySelector(`[data-nc-pick-idx="${idx}"] [data-nc-pick-q]`).value.trim();
    host.innerHTML = '<span class="nc-muted">Поиск…</span>';
    let res;
    try { res = await api.norms.docs({ q, limit: 20 }); }
    catch (e) { host.innerHTML = `<span class="cp-error">${esc(apiErrorText(e))}</span>`; return; }
    if (!res.items.length) { host.innerHTML = '<span class="nc-muted">Ничего не найдено в реестре.</span>'; return; }
    host.innerHTML = res.items.map((d) => {
      const has = d.has_text ? ' <span class="nc-muted">(уже есть текст)</span>' : '';
      return `<button type="button" class="nc-batch-doc" data-nc-pick-doc="${idx}" data-doc-id="${d.id}" data-doc-code="${esc(d.code)}">
        ${esc(d.code)} <span class="nc-muted">${esc(d.title || '')}</span>${has}</button>`;
    }).join('');
  }

  async function manualUpload(idx, docId, docCode) {
    const r = state.results[idx];
    const file = state.files[r.fileIndex];
    if (!file) { showNotification('Файл недоступен', 'error'); return; }
    try {
      const up = await api.norms.uploadVersion(docId, file);
      const v = up.version;
      Object.assign(r, {
        outcome: 'matched', doc_id: docId, doc_code: docCode, version_id: v.id,
        duplicate: false, error: null, parse: v,
      });
      showNotification(`Загружено в ${docCode}, идёт разбор`, 'success');
      renderAll();
      pollMatched();
    } catch (e) { showNotification(apiErrorText(e), 'error'); }
  }
}

// ── Актуальность: строка истории и форма ручной отметки (НК-2b-1) ──────────────

function checkRow(c) {
  const method = CHECK_METHOD_LABELS[c.method] || c.method;
  const by = c.checked_by ? ` · ${esc(c.checked_by)}` : '';
  const parts = [];
  if (c.error) parts.push(`<span class="nc-chk-err">ошибка: ${esc(c.error)}</span>`);
  else if (c.result_status) parts.push(esc(statusLabel(c.result_status)));
  if (c.result_replaced_by_code) parts.push(`взамен: ${esc(c.result_replaced_by_code)}`);
  if (c.conflict_with_manual) parts.push('<span class="nc-chk-conflict">расхождение с ручной</span>');
  const src = c.source_url
    ? ` <a href="${esc(c.source_url)}" target="_blank" rel="noopener">источник</a>` : '';
  const note = c.raw_excerpt ? `<div class="nc-check-note">${esc(c.raw_excerpt)}</div>` : '';
  return `<li class="nc-check-item">
    <div class="nc-check-head"><b>${esc(method)}</b> · ${esc(fmtDateTime(c.checked_at))}${by}</div>
    <div class="nc-check-res">${parts.join(' · ') || '—'}${src}</div>
    ${note}
  </li>`;
}

function openCheckForm(doc) {
  return openDialog({
    title: `Отметить проверку: ${doc.code}`,
    confirmLabel: 'Сохранить',
    bodyHtml: `
      <label class="modal-field"><span class="modal-field-label">Юридический статус</span>
        <select name="status" class="modal-select">
          ${STATUSES.map(s => `<option value="${s}"${s === doc.status ? ' selected' : ''}>${esc(statusLabel(s))}</option>`).join('')}
        </select></label>
      ${field('effective_from', 'Дата введения', doc.effective_from ? String(doc.effective_from).slice(0, 10) : '', 'type="date"')}
      ${field('approved_at', 'Дата утверждения', '', 'type="date"')}
      <label class="modal-field nc-replaced-row"${doc.status === 'replaced' ? '' : ' hidden'}>
        <span class="modal-field-label">Чем заменён (шифр из реестра или текстом)</span>
        <input name="replaced_by_code" value="${esc(doc.replaces_text ?? '')}" placeholder="напр. СП 42.13330.2016" /></label>
      ${field('source_url', 'Ссылка-источник', '', 'placeholder="https://protect.gost.ru/..."')}
      <label class="modal-field"><span class="modal-field-label">Комментарий</span>
        <textarea name="comment" rows="2" class="modal-textarea"></textarea></label>`,
    onMount: (dlg) => {
      const sel = dlg.querySelector('[name="status"]');
      const row = dlg.querySelector('.nc-replaced-row');
      sel.addEventListener('change', () => { row.hidden = sel.value !== 'replaced'; });
    },
    onConfirm: async (dlg) => {
      const g = (n) => dlg.querySelector(`[name="${n}"]`);
      const status = g('status').value;
      const body = {
        status,
        effective_from: g('effective_from').value || null,
        approved_at: g('approved_at').value || null,
        replaced_by_code: status === 'replaced' ? (g('replaced_by_code').value.trim() || null) : null,
        source_url: g('source_url').value.trim() || null,
        comment: g('comment').value.trim() || null,
      };
      try { return await api.norms.addCheck(doc.id, body); }
      catch (e) { showNotification(apiErrorText(e), 'error'); return false; }
    },
  });
}
